import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactDOM from 'react-dom/client';

export interface IDialog {
  isOpen?: boolean;
  title: React.ReactNode;
  content: React.ReactNode;
  hasCancel?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  cancelText?: React.ReactNode;
  confirmText?: React.ReactNode;
  confirmType?: 'danger' | 'primary';
  useProtoal?: boolean;
  confirmTimeout?: number;
}

export default function Dialog(props: IDialog) {
  const {
    isOpen = false,
    useProtoal = true,
    title,
    content,
    hasCancel = true,
    onCancel,
    onConfirm,
    cancelText,
    confirmText,
    confirmType = 'danger',
    confirmTimeout,
  } = props;

  const [timer, setTimer] = useState<number | undefined>(confirmTimeout);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // 如果有倒计时，开启倒计时
    if (typeof confirmTimeout === 'number' && !intervalRef.current && isOpen) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          console.log('setTimer  prev:', prev);
          if (prev === 0) {
            clearInterval(intervalRef.current!);
            intervalRef.current = null;
            onConfirm?.();
            setTimer(confirmTimeout);
            return prev;
          }
          return prev! - 1000;
        });
      }, 1000);
    }
    return () => {
      intervalRef.current && clearInterval(intervalRef.current);
      intervalRef.current = null;
      setTimer(confirmTimeout);
    };
  }, [confirmTimeout, isOpen]);

  if (!isOpen) return null;
  const component = (
    <div
      className={`fixed top-0 left-0 w-full h-full`}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className={`w-full h-full bg-black bg-opacity-50`}>
        <div className="absolute flex flex-col bg-white w-80 h-39 rd-2 left-1/2 top-1/2 -translate-1/2 p-6">
          <span className="text-size-3.5 text-mx-secondary font-bold lh-5.5">{title}</span>
          <span className="text-size-3.5 text-mx-secondary mt-2 flex-1 lh-5.5">{content}</span>
          <div className="flex flex-row flex-justify-end">
            {
              hasCancel && (<div
                className="h-8 text-mx-secondary rd-1.5 px-4 flex flex-items-center flex-justify-center border-1 border-#ECEEF3 border-solid cursor-pointer text-size-3.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel?.();
                }}
              >{cancelText || '取消'}</div>)
            }
            <button
              className={`h-8 rd-1.5 px-4 ml-4 flex flex-items-center flex-justify-center text-white text-size-3.5 hover:opacity-90  outline-none border-none cursor-pointer ${confirmType === 'danger' ? 'bg-#FF584C' : 'bg-mx-primary'
                }`}
              onClick={(e) => {
                e.stopPropagation();
                onConfirm?.();
              }}
            >
              {confirmText || '删除'}
              {typeof timer === 'number' && <span>({timer / 1000}s)</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!useProtoal) {
    return component;
  }

  return createPortal(component, document.body);
}

export const showDialog = async (props: IDialog) => {
  return new Promise((resolve, reject) => {
    const div = document.createElement('div');
    document.body.appendChild(div);
    const close = () => {
      document.body.removeChild(div);
      reject();
    };
    const confirm = () => {
      document.body.removeChild(div);
      resolve(true);
    };
    const dialog = <Dialog {...props} isOpen useProtoal={false} onCancel={close} onConfirm={confirm} />;
    ReactDOM.createRoot(div).render(dialog);
  });
};
